/**
 * Centralized Gatha Library — Single source of truth for all mindfulness text.
 * Fetches from /api/v1/gathas on first use, caches grouped by type.
 * Falls back to hardcoded array if API is unreachable.
 */

const API_BASE = process.env.REACT_APP_RESOURCE_API || 'https://neev-api.onrender.com';

// --- Lazy API cache ---

let _cache = null;  // { gatha: [], sankalp: [], metta: [], eye_exercise: [], desk_stretch: [] }
let _fetching = null;
let _isFallback = false;  // true if cache is from fallback (allows retry)

const FALLBACK_GATHAS = [
  { id: 0, text_en: "Breathing in, I calm my body. Breathing out, I smile.", text_mr: "श्वास घेताना, मी माझे शरीर शांत करतो. श्वास सोडताना, मी हसतो.", type: "gatha" },
  { id: 0, text_en: "Smile, breathe, and go slowly.", text_mr: "हसा, श्वास घ्या, आणि हळू चला.", type: "gatha" },
  { id: 0, text_en: "Because you are alive, everything is possible.", text_mr: "तुम्ही जिवंत आहात म्हणून, सर्व काही शक्य आहे.", type: "gatha" },
  { id: 0, text_en: "Dwelling in the present moment, I know this is a wonderful moment!", text_mr: "या क्षणात राहताना, मला माहीत आहे हा एक अद्भुत क्षण आहे!", type: "gatha" },
  { id: 0, text_en: "Walk as if you are kissing the Earth with your feet.", text_mr: "असे चाला जणू तुम्ही पायांनी पृथ्वीचे चुंबन घेत आहात.", type: "gatha" },
];

const FALLBACK_GROUPED = groupByType(FALLBACK_GATHAS);

function groupByType(rows) {
  const grouped = { gatha: [], sankalp: [], metta: [], eye_exercise: [], desk_stretch: [] };
  for (const row of rows) {
    if (grouped[row.type]) {
      grouped[row.type].push(row);
    }
  }
  return grouped;
}

/**
 * Lazy-load gathas from API on first use. Caches in module scope.
 * On failure, returns fallback but allows retry on next call.
 */
async function ensureLoaded() {
  if (_cache && !_isFallback) return _cache;
  if (!_fetching) {
    _fetching = fetch(`${API_BASE}/api/v1/gathas`)
      .then(r => r.json())
      .then(d => {
        _cache = groupByType(d.data || []);
        _isFallback = false;
        _fetching = null;
        return _cache;
      })
      .catch(() => {
        _cache = FALLBACK_GROUPED;
        _isFallback = true;
        _fetching = null;
        return _cache;
      });
  }
  return _fetching;
}

/**
 * Get the cached data synchronously (returns fallback if not loaded yet).
 * For components that can't await.
 */
function getCacheSync() {
  return _cache || FALLBACK_GROUPED;
}

// --- Public API ---

/**
 * Get a random entry of a specific type.
 * Async — triggers API fetch if not cached yet.
 */
export async function getRandomByType(type) {
  const cache = await ensureLoaded();
  const arr = cache[type] || cache.gatha;
  if (!arr || arr.length === 0) return FALLBACK_GATHAS[0];
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Get random content for processing overlays (gatha, sankalp, or metta).
 * Returns { text_en, text_mr, type } — sync with fallback.
 */
export function getRandomOverlayContent() {
  const cache = getCacheSync();
  const pool = [...(cache.gatha || []), ...(cache.sankalp || []), ...(cache.metta || [])];
  if (pool.length === 0) return FALLBACK_GATHAS[0];
  return pool[Math.floor(Math.random() * pool.length)];
}

/**
 * Get today's daily sankalp (consistent within a day, day-of-year index).
 * Async — triggers API fetch if not cached yet.
 */
export async function getDailySankalp() {
  const cache = await ensureLoaded();
  const sankalps = cache.sankalp || [];
  if (sankalps.length === 0) return null;
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((now - start) / 86400000);
  return sankalps[dayOfYear % sankalps.length];
}

/**
 * Get all entries of a specific type (for MeditationBell rotation).
 * Async — triggers API fetch if not cached yet.
 */
export async function getListByType(type) {
  const cache = await ensureLoaded();
  return cache[type] || [];
}

/**
 * Get all gathas (for MeditationBell — backward compat).
 */
export async function getGathasList() {
  return getListByType('gatha');
}

/**
 * Pre-warm the cache (call from layout components on mount).
 * Fire-and-forget, no await needed.
 */
export function preloadGathas() {
  ensureLoaded();
}

// --- Kept: Static content (no DB dependency) ---

// Compassionate empty state messages — keyed by entity type
export const EMPTY_STATE_MESSAGES = {
  default: {
    title: "No results found",
    description: "The search found stillness. Perhaps try different words.",
  },
  schools: {
    title: "No schools here yet",
    description: "Like an empty bowl, ready to receive.",
  },
  teachers: {
    title: "No teachers here yet",
    description: "Each one will arrive in their own time.",
  },
  officers: {
    title: "No officers here yet",
    description: "The space awaits.",
  },
  volunteers: {
    title: "No volunteers here yet",
    description: "Every journey begins with a single step.",
  },
  divisions: {
    title: "No divisions here yet",
    description: "Room to grow.",
  },
  classes: {
    title: "No classes here yet",
    description: "Like an open field, full of possibility.",
  },
  lessonContent: {
    title: "No content here yet",
    description: "Seeds waiting to be planted.",
  },
  dashboard: {
    title: "Nothing to show right now",
    description: "Space to breathe.",
  },
  gathas: {
    title: "No mindful content yet",
    description: "The silence speaks.",
  },
};

// Time-of-day mindful greetings
export function getMindfulGreeting() {
  const hour = new Date().getHours();
  if (hour < 6) return "The world is still. A quiet moment before the day begins.";
  if (hour < 12) return "Good morning. A fresh day to begin again.";
  if (hour < 17) return "Good afternoon. Pause, breathe, continue.";
  if (hour < 21) return "Good evening. As the day settles, so can the mind.";
  return "The day is winding down. Rest well.";
}

// Teacher app mindful subtext (beneath greeting)
export function getMindfulSubtext() {
  const hour = new Date().getHours();
  if (hour < 6) return "The world is still. A quiet start.";
  if (hour < 12) return "A fresh day to begin again.";
  if (hour < 17) return "Pause, breathe, continue.";
  if (hour < 21) return "As the day settles, so can the mind.";
  return "Rest well.";
}

// --- Sync helpers for components that can't be async ---

/**
 * Get a random overlay gatha text (sync, string only — for processing overlays).
 * Returns just the text_en string for backward compatibility.
 */
export function getOverlayGathaText() {
  const entry = getRandomOverlayContent();
  return entry.text_en;
}

/**
 * Get today's footer gatha (sync). Returns { text, source }.
 */
export function getTodayFooterGatha() {
  const cache = getCacheSync();
  const gathas = cache.gatha || [];
  const entry = gathas.length === 0 ? FALLBACK_GATHAS[0] : gathas[(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 0);
    const dayOfYear = Math.floor((now - start) / 86400000);
    return dayOfYear % gathas.length;
  })()];
  return { text: entry.text_en, source: entry.source || null };
}

/**
 * Get a random login gatha (sync). Returns { text, source }.
 */
export function getLoginGatha() {
  const cache = getCacheSync();
  const gathas = cache.gatha || [];
  const entry = gathas.length === 0 ? FALLBACK_GATHAS[0] : gathas[Math.floor(Math.random() * gathas.length)];
  return { text: entry.text_en, source: entry.source || null };
}
